import * as RadixCheckbox from '@radix-ui/react-checkbox';
import { useId } from 'react';

import { cn } from '~/chords/lib/cn';

import type { ComponentProps } from '../ComponentProps';
import { getClassNameOverride } from '../getClassNameOverride';
import { Icon } from '../Icon';

type RootProps = Omit<
  React.ComponentProps<typeof RadixCheckbox.Root>,
  'defaultState' | 'className'
>;

export type CheckboxProps = ComponentProps<{
  invalid?: boolean;
  label?: string;
}> &
  Required<Pick<RootProps, 'checked'>> &
  RootProps;

export function Checkbox({
  checked,
  invalid,
  dangerouslyOverrideClassName,
  disabled,
  label,
  ...props
}: CheckboxProps) {
  const className = getClassNameOverride(
    { dangerouslyOverrideClassName },
    'Checkbox',
  );
  const id = useId();
  const isChecked = checked === true || checked === 'indeterminate';

  return (
    <label
      htmlFor={id}
      aria-disabled={disabled}
      className={cn(
        'text-default',
        'font-normal',
        'cursor-pointer',
        invalid && 'text-error-default',
        disabled && 'text-disabled',
        disabled && 'cursor-not-allowed',
        'gap-sm',
        'flex',
        'items-center',
        className,
      )}
    >
      <RadixCheckbox.Root
        id={id}
        className={cn(
          'hover:outline',
          'hover:outline-hover',
          'hover:outline-2',
          'relative',
          'rounded-xs',
          'border-current',
          'border',
          'outline-offset-[-1px]',
          'size-md',
          'transition-all',
          isChecked && 'bg-primary-default',
          isChecked && 'text-primary-default',
          invalid && isChecked && 'bg-error-default',
          invalid && isChecked && 'text-error-default',
          'disabled:cursor-not-allowed',
          'disabled:text-disabled',
          'disabled:bg-transparent',
        )}
        checked={checked}
        disabled={disabled}
        {...props}
      >
        <RadixCheckbox.Indicator
          className={cn(
            'text-default',
            'block [&_i:before]:grid [&_i]:grid',
            'size-full [&_i:before]:size-full [&_i]:size-full',
            '[&_i:before]:place-items-center [&_i]:place-items-center',
            '[&_i:before]:leading-[0] [&_i]:leading-[0]',
            '[&_i:before]:align-middle [&_i]:align-middle',
            disabled && 'text-[var(--color-ghost-white-900)]',
            !disabled && invalid && 'text-black',
          )}
        >
          <Icon
            type={checked === 'indeterminate' ? 'minus' : 'check'}
            size="xs"
          />
        </RadixCheckbox.Indicator>
      </RadixCheckbox.Root>
      {label ? <span className="text-caption-md">{label}</span> : null}
    </label>
  );
}

Checkbox.displayName = 'Checkbox';
